import React from 'react';
import {
  DollarSign,
  Package,
  FileText,
  Image as ImageIcon,
  RefreshCw,
  ShieldCheck,
  Circle,
} from 'lucide-react';
import { cn } from '../../lib/cn';

export type ActivityEventType =
  | 'price'
  | 'stock'
  | 'description'
  | 'media'
  | 'sync'
  | 'moderation'
  | 'other';

export interface ActivityEvent {
  id: string;
  at: Date | string;
  actor?: string;
  type: ActivityEventType;
  title: string;
  description?: string;
  /** Свободная разметка под заголовком (списки артикулов, разобранная ошибка). Описание рисуется ниже. */
  body?: React.ReactNode;
  diff?: { before: string; after: string };
}

export interface ActivityTimelineProps {
  events: ActivityEvent[];
  emptyState?: React.ReactNode;
  className?: string;
}

const typeIcon: Record<ActivityEventType, React.ReactNode> = {
  price: <DollarSign className="h-3 w-3" />,
  stock: <Package className="h-3 w-3" />,
  description: <FileText className="h-3 w-3" />,
  media: <ImageIcon className="h-3 w-3" />,
  sync: <RefreshCw className="h-3 w-3" />,
  moderation: <ShieldCheck className="h-3 w-3" />,
  other: <Circle className="h-3 w-3" />,
};

const typeColor: Record<ActivityEventType, string> = {
  price: 'bg-green-100 text-green-700 ring-green-200 dark:bg-green-950 dark:text-green-300 dark:ring-green-900',
  stock: 'bg-blue-100 text-blue-700 ring-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:ring-blue-900',
  description: 'bg-gray-100 text-gray-700 ring-gray-200 dark:bg-zinc-800 dark:text-zinc-200 dark:ring-zinc-700',
  media: 'bg-purple-100 text-purple-700 ring-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:ring-purple-900',
  sync: 'bg-indigo-100 text-indigo-700 ring-indigo-200 dark:bg-indigo-950 dark:text-indigo-300 dark:ring-indigo-900',
  moderation: 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:ring-amber-900',
  other: 'bg-gray-100 text-gray-600 ring-gray-200 dark:bg-zinc-800 dark:text-zinc-300 dark:ring-zinc-700',
};

const formatAt = (input: Date | string): string => {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * ActivityTimeline — vertical timeline of typed events.
 */
export const ActivityTimeline: React.FC<ActivityTimelineProps> = ({
  events,
  emptyState,
  className,
}) => {
  if (!events || events.length === 0) {
    return (
      <div className={cn('text-sm text-gray-500 dark:text-zinc-400 italic px-2 py-6 text-center', className)}>
        {emptyState ?? 'Событий пока нет'}
      </div>
    );
  }

  return (
    <ol className={cn('relative', className)}>
      <span
        className="absolute left-3 top-2 bottom-2 w-px bg-gray-200 dark:bg-zinc-800"
        aria-hidden="true"
      />
      {events.map((e) => (
        <li key={e.id} className="relative pl-9 pb-4 last:pb-0">
          <span
            className={cn(
              'absolute left-0 top-0 h-6 w-6 rounded-full flex items-center justify-center ring-2',
              typeColor[e.type],
            )}
          >
            {typeIcon[e.type]}
          </span>
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-medium text-gray-900 dark:text-zinc-100">{e.title}</span>
            <span className="text-2xs text-gray-500 dark:text-zinc-400">{formatAt(e.at)}</span>
            {e.actor && (
              <span className="text-2xs text-gray-500 dark:text-zinc-400">· {e.actor}</span>
            )}
          </div>
          {e.body && <div className="mt-0.5 text-xs text-gray-600 dark:text-zinc-300">{e.body}</div>}
          {e.description && (
            <div className="mt-0.5 text-xs text-gray-600 dark:text-zinc-300">{e.description}</div>
          )}
          {e.diff && (
            <div className="mt-1.5 text-xs flex items-center gap-2 flex-wrap">
              <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-700 line-through dark:bg-red-950 dark:text-red-300">
                {e.diff.before}
              </span>
              <span className="text-gray-400 dark:text-zinc-500">→</span>
              <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300">
                {e.diff.after}
              </span>
            </div>
          )}
        </li>
      ))}
    </ol>
  );
};

export default ActivityTimeline;
